import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import * as moment from "moment";

import { handleApiError } from "@utils/errorHandler";
import {
  TABLE_PURCHASE_ORDER_LIST,
  FILTER_TYPE_BUTTON,
  FILTER_TYPE_MULTI_SELECT_DROPDOWN,
  FILTER_TYPE_DATEPICKER,
  PURCHASE_ORDER_LIST_ENDPOINT,
} from "@utils/constants";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import UserInfo from "@utils/UserInfo";
import Action from "./components/Action/Action";
import GeneralInfo from "./components/GeneralInfo/GeneralInfo";
import { columnHeader, isEmpty } from "@utils/utils";
import { purchaseOrderStatus } from "@utils/filterOptions";
import { renderPriceWithCurrencyWithoutUnit } from "@utils/utils";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefreshStatus,
    columnList,
    createNewPurchaseOrder,
    countryId,
    entityId,
    page,
    siteName,
    siteOpportunityNumber,
    companyData,
  } = props;

  const [data, setData] = useState([]);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);
  const [startDate, setStartDate] = useState(
    moment(new Date(new Date().getFullYear(), 0, 1)).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [additionalFilters, setAdditionalFilters] = useState([]);

  let columns = [];
  const generalParams = useGetGeneralParameters();
  const { countryCurrencyPosition } = generalParams;
  const currencyList = generalParams.countryCurrency;

  const [selectedFilter, setSelectedFilter] = useState([]);

  columns = [
    {
      Header: t("PURCHASE_ORDER_REFERENCE"),
      title: "PURCHASE_ORDER_REFERENCE",
      accessor: "reference",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="reference" />
      ),
    },
    {
      Header: t("PURCHASE_ORDER_CENTRE"),
      title: "PURCHASE_ORDER_CENTRE",
      accessor: "supplier",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="center" companyData={companyData} />
      ),
    },
    {
      Header: t("SUPPLIER"),
      title: "SUPPLIER",
      accessor: "supplier",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="supplier" />
      ),
    },
    {
      Header: t("SITES"),
      title: "SITES",
      accessor: "sites",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="sites" />
      ),
    },
    {
      Header: t("PURCHASE_ORDER_ORDER_DATE"),
      title: "PURCHASE_ORDER_ORDER_DATE",
      accessor: "orderDate",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="orderDate" />
      ),
    },
    {
      Header: t("AMOUNT_EXCL_TAX"),
      title: "AMOUNT_EXCL_TAX",
      accessor: "totalAmountExclTax",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="totalAmountExclTax" />
      ),
    },
    {
      Header: t("PURCHASE_ORDER_HESUS_CONTACT"),
      title: "PURCHASE_ORDER_HESUS_CONTACT",
      accessor: "buyer",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="buyer" />
      ),
    },
    {
      Header: t("STATUS"),
      title: "STATUS",
      accessor: "status",
      disableFilters: true,
      Cell: ({ cell: { value } }) => (
        <GeneralInfo data={value} column="status" />
      ),
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <Action
          data={value}
          column={column}
          updateRefreshStatus={updateRefreshStatus}
          page={page}
          entityId={entityId}
          siteName={siteName}
          siteOpportunityNumber={siteOpportunityNumber}
          countryId={countryId}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    });

    // save request data,
    let purchaseStatus = [];
    if (additionalFilters?.length > 0) {
      const statusTypeFilter = additionalFilters.filter(
        (filterItem) => filterItem.column === "purchaseOrderStatus"
      );
      if (statusTypeFilter?.length > 0)
        purchaseStatus = statusTypeFilter?.map((item) => item.value);
    }
    // Set the loading state
    setLoading(true);
    axios
      .get(PURCHASE_ORDER_LIST_ENDPOINT, {
        params: {
          countries: JSON.stringify([countryId]),
          status: JSON.stringify(purchaseStatus),
          fromDate: moment(startDate).format("YYYY-MM-DD"),
          toDate: moment(endDate).format("YYYY-MM-DD"),
          limit: pageSize,
          page: currentPage,
          orderBy: sort ? sort.dir : "desc",
          orderColumn: sort ? sort.column : "id",
          search: globalFilter || "",
          ...(page === "site" ? { siteId: entityId } : { companyId: entityId }),
        },
      })
      .then((response) => {
        if (response?.data?.purchaseOrders?.length > 0) {
          const purchaseOrdersList = response?.data?.purchaseOrders;
          const responseData = purchaseOrdersList.map((purchaseOrder) => {
            const tempData = { ...purchaseOrder };
            tempData.supplier = {
              supplier: tempData.supplier,
              status: tempData.status,
            };
            tempData.sites = {
              sites: [
                ...new Map(
                  tempData.sites
                    .filter((item) => item?.id != null)
                    .map((item) => [item.id, item])
                ).values(),
              ],
              status: tempData.status,
              pId: tempData.id,
            };
            tempData.reference = {
              reference: tempData.reference,
              status: tempData.status,
            };
            tempData.orderDate = {
              orderDate: tempData.orderDate
                ? tempData.orderDate
                : null,
              status: tempData.status,
            };
            tempData.totalAmountExclTax = {
              totalAmountExclTax: renderPriceWithCurrencyWithoutUnit(
                tempData.totalAmountExclTax,
                currencyList[countryId],
                countryCurrencyPosition[countryId]
              ),
              status: tempData.status,
            };
            tempData.buyer = {
              buyer: tempData.buyer,
              status: tempData.status,
            };
            tempData.action = {
              id: tempData.id,
              status: tempData.status,
              reference: tempData.reference.reference,
            };
            tempData.status = {
              statusLabel: tempData.statusLabel,
              status: tempData.status,
            };
            return tempData;
          });
          setData([...responseData]);
          setPageCount(response?.data?.filteredRecords || 0);
        } else {
          setData([]);
          setPageCount(0);
        }
        setLoading(false);
      })
      .catch((error) => {
        setData([]);
        setPageCount(0);
        setLoading(false);
        handleApiError(error);
      });
  };

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage === 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        appliedFilters: requestData.additionalFilters,
        currentPage: requestData.currentPage,
      });
    }
  }, [additionalFilters, startDate, endDate]);

  const onDateChange = (name, e) => {
    if (name === "startDate") {
      setStartDate(moment(e.toDate()).format("YYYY-MM-DD"));
    }
    if (name === "endDate") {
      setEndDate(moment(e.toDate()).format("YYYY-MM-DD"));
    }
  };
  console.log("pageCount", pageCount);

  return (
    <EncloseTable
      type={TABLE_PURCHASE_ORDER_LIST}
      columns={columns}
      data={data}
      setData={setData}
      initialPageSize={40}
      createNewAction={createNewPurchaseOrder}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      setPageCount={setPageCount}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      fetchRecords={fetchRecords}
      loading={loading}
      setAdditionalFilters={setAdditionalFilters}
      additionalFilters={additionalFilters}
      selectedFilter={selectedFilter}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      extraFilters={[
        {
          type: FILTER_TYPE_MULTI_SELECT_DROPDOWN,
          label: t("STATUS"),
          placeholder: t("STATUS"),
          name: "purchaseOrderStatus",
          labelClass: "purchaseOrder-list__dropdown-labell",
          containerClass: "purchaseOrder-list__status-list-dropdown-container",
          defaultOption: "Nothing Selected",
          options: purchaseOrderStatus(t),
        },
        {
          type: FILTER_TYPE_DATEPICKER,
          label: t("PURCHASE_ORDERS_FILTER_FROM_DATE"),
          placeholder: t("PURCHASE_ORDERS_FILTER_FROM_DATE"),
          name: "startDate",
          format: "YYYY-MM-DD",
          value: { startDate },
          onDateChange: { onDateChange },
        },
        {
          type: FILTER_TYPE_DATEPICKER,
          label: t("PURCHASE_ORDERS_FILTER_TO_DATE"),
          placeholder: t("PURCHASE_ORDERS_FILTER_TO_DATE"),
          name: "endDate",
          format: "YYYY-MM-DD",
          value: { endDate },
          onDateChange: { onDateChange },
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
  companyData: PropTypes.object,
};
PrepareDataTable.defaultProps = {
  companyData: null,
};

export default withTranslation()(PrepareDataTable);
